import type { UserStatus } from '@/gql/graphql';
import { int, oneOf, text, uuid, yesNo } from '@/hooks/useUrlState';
import { userStatusLabel, userTypeLabel } from '@/lib/labels';

/** Every `UserStatus` in the order the filter lists them, with its label; the `Record` makes the compiler flag a status added to the schema but not here. */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  IMPORTED: userStatusLabel('IMPORTED'),
  PROVISIONAL: userStatusLabel('PROVISIONAL'),
  CREATED: userStatusLabel('CREATED'),
  ACTIVE: userStatusLabel('ACTIVE'),
  BLOCKED: userStatusLabel('BLOCKED'),
  DELETED: userStatusLabel('DELETED'),
};

/** `UserType` plus CUSTOMER (the filter's "customers only"), in the order the filter lists them. */
export type UserTypeFilter = 'CUSTOMER' | 'EMPLOYEE' | 'CONTRACTOR' | 'ADMIN';
export const USER_TYPE_LABELS: Record<UserTypeFilter, string> = {
  CUSTOMER: userTypeLabel(null), // a customer has no `UserType`
  EMPLOYEE: userTypeLabel('EMPLOYEE'),
  CONTRACTOR: userTypeLabel('CONTRACTOR'),
  ADMIN: userTypeLabel('ADMIN'),
};

export type UsersSortColumn = 'name' | 'email' | 'passkeys' | 'lastLogin' | 'status';

/** DataGrid column `id` → GraphQL sort field for server-side sort; its keys are the values `sort` accepts. */
export const USER_COLUMN_SORT: Record<UsersSortColumn, string> = {
  name: 'firstName',
  email: 'email',
  passkeys: 'passkeyCount',
  lastLogin: 'lastLoginAt',
  status: 'status',
};

export const USERS_DEFAULT_SORT: { sort: UsersSortColumn; dir: 'asc' | 'desc' } = { sort: 'name', dir: 'asc' };

/** The /users query string, in this order (see README "List pages, URL state"). */
export const usersUrlSchema = {
  search: text(),
  status: oneOf(USER_STATUS_LABELS, ''),
  type: oneOf(USER_TYPE_LABELS, ''),
  marketing: yesNo(),
  golden: yesNo(),
  role: uuid(),
  scope: uuid(),
  sort: oneOf(USER_COLUMN_SORT, USERS_DEFAULT_SORT.sort),
  dir: oneOf<'asc' | 'desc', 'asc' | 'desc'>({ asc: true, desc: true }, USERS_DEFAULT_SORT.dir),
  page: int({ min: 1 }, 1),
  size: int({ min: 1, max: 500 }, 25),
};

/** The keys that live in the collapsible Filters panel: the panel opens on mount when any is set. */
export const USERS_ADVANCED_KEYS = [
  'status',
  'type',
  'marketing',
  'golden',
  'role',
  'scope',
] as const satisfies readonly (keyof typeof usersUrlSchema)[];
